import {
    GetReportExportDownloadResponse,
    GetReportResponse,
    GetReportsRequestParams,
    GetReportsResponse,
    SaveReportRequestBody,
    UpdateReportRequestBody,
    UpdateReportResponse,
} from '@/hooks'
import useAuthStore from '../../../stores/useAuthStore/useAuthStore'
import { useAxiosInstance } from '../useAxiosInstance'
import { AxiosError } from 'axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { checkReportNameExists } from '../utils/checkReportNameExists'

export const useReportsApi = () => {
    const accessToken = useAuthStore((state) => state.accessToken)
    const axiosInstance = useAxiosInstance(accessToken)
    const queryClient = useQueryClient()

    const customerId =
        useAuthStore((state) => state.customer?.customerId.toString()) ?? ''

    const useSaveReportMutation = () => {
        return useMutation<
            GetReportResponse,
            AxiosError,
            SaveReportRequestBody
        >({
            mutationFn: async (payload) => {
                try {
                    const response =
                        await axiosInstance.post<GetReportResponse>(
                            `/api/v1/customers/${customerId}/custom-reports`,
                            payload
                        )
                    return response.data
                } catch (error) {
                    if (error instanceof AxiosError) {
                        const responseData = error.response?.data as
                            | { message?: string }
                            | undefined

                        if (
                            error.response?.status === 409 &&
                            responseData?.message
                        ) {
                            if (responseData.message.includes('Conflict')) {
                                throw error
                            }
                        }
                    }
                    throw error
                }
            },
            onSuccess: async () => {
                await queryClient.invalidateQueries({
                    queryKey: ['reports', customerId, accessToken],
                })
            },
        })
    }

    const useDeleteReportMutation = () => {
        return useMutation<unknown, AxiosError, string>({
            mutationFn: async (customReportId) => {
                await axiosInstance.delete(
                    `/api/v1/customers/${customerId}/custom-reports/${customReportId}`
                )
            },
            onSuccess: async () => {
                await queryClient.invalidateQueries({
                    queryKey: ['reports', customerId, accessToken],
                })
            },
        })
    }

    const useGetReports = (params: GetReportsRequestParams) => {
        return useQuery({
            queryKey: ['reports', customerId, accessToken, params],
            queryFn: async () => {
                const response = await axiosInstance.get<GetReportsResponse>(
                    `/api/v1/customers/${customerId}/custom-reports`,
                    { params }
                )

                return response.data
            },
            enabled: !!accessToken && !!customerId,
            placeholderData: (prev) => prev,
        })
    }

    const useGetReport = (
        reportId: string,
        opts: { enabled: boolean } = { enabled: true }
    ) => {
        return useQuery({
            queryKey: ['reports', customerId, accessToken, reportId],
            queryFn: async () => {
                const response = await axiosInstance.get<GetReportResponse>(
                    `/api/v1/customers/${customerId}/custom-reports/${reportId}`
                )

                return response.data
            },
            enabled:
                opts.enabled && !!accessToken && !!customerId && !!reportId,
        })
    }

    const useUpdateReportMutation = () => {
        return useMutation<
            UpdateReportResponse,
            AxiosError,
            { customReportId: string; data: UpdateReportRequestBody }
        >({
            mutationFn: async ({ customReportId, data }) => {
                try {
                    const response =
                        await axiosInstance.patch<UpdateReportResponse>(
                            `/api/v1/customers/${customerId}/custom-reports/${customReportId}`,
                            data
                        )
                    return response.data
                } catch (error) {
                    if (error instanceof AxiosError) {
                        const responseData = error.response?.data as
                            | { message?: string }
                            | undefined

                        if (
                            error.response?.status === 409 &&
                            responseData?.message
                        ) {
                            if (responseData.message.includes('Conflict')) {
                                throw error
                            }
                        }
                    }
                    throw error
                }
            },
            onSuccess: async () => {
                await queryClient.invalidateQueries({
                    queryKey: ['reports', customerId, accessToken],
                })
            },
        })
    }

    const useReportNameExists = (
        name: string,
        opts: useReportNameExistsQueryOpts = { enabled: true }
    ) => {
        const limit = 1000
        const params = {
            name,
            limit,
        }

        return useQuery({
            queryKey: ['reports', customerId, accessToken, params],
            queryFn: async () => {
                try {
                    const response =
                        await axiosInstance.get<GetReportsResponse>(
                            `/api/v1/customers/${customerId}/custom-reports`,
                            {
                                params,
                            }
                        )

                    const processedResponseData = checkReportNameExists(
                        name,
                        response.data
                    )
                    opts.onSuccess?.(processedResponseData)
                    return processedResponseData
                } catch (error) {
                    opts.onError?.()
                    throw error
                }
            },
            enabled: !!accessToken && !!customerId && opts.enabled,
            refetchOnWindowFocus: false,
        })
    }

    const useGetReportExportDownloadUrl = (
        exportId: string,
        opts: { enabled: boolean } = { enabled: true }
    ) => {
        return useQuery({
            queryKey: ['report-export-download', accessToken, exportId],
            queryFn: async () => {
                const response =
                    await axiosInstance.get<GetReportExportDownloadResponse>(
                        `/api/v1/users/me/exports/${exportId}`
                    )

                return response.data
            },
            enabled: opts.enabled && !!accessToken && !!exportId,
        })
    }

    return {
        useGetReports,
        useGetReport,
        useGetReportExportDownloadUrl,
        useSaveReportMutation,
        useDeleteReportMutation,
        useUpdateReportMutation,
        useReportNameExists,
    }
}

type useReportNameExistsQueryOpts = {
    onSuccess?: (data: boolean) => void
    onError?: () => void
    enabled?: boolean
}
